import { useCallback, useContext } from 'react';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { fetchCampaignsByIdEDM } from '../../fetch/campaigns';
import { fetchExperiment, parseMeasurementTypeEnum } from '../../fetch/experiment';
import { EDIT_INTENT } from '../constants/intents';
import { errorMessages } from '../errors';
import { validateCampaignCount } from '../validate';
import { ADD_KEYWORDS_BY_ENTRY, ADD_KEYWORDS_BY_SELECTION } from './keyword/keyword-methods';
import { STATUS_SCHEDULED } from '../../landing-page/statuses';

export const useSelectCampaignsRepository = () => {
  const { scope } = useContext(ScopeContext);

  const { state: wizardState, send } = useWizardService();
  const { wizardData } = wizardState.context;
  const isEdit = wizardData.intent === EDIT_INTENT;

  const i18n = useContext(I18nContext);

  const load = useCallback(() => new Promise((resolve) => {
    if (isEdit) {
      if (!wizardData.isInitialized) {
        const onFailure = (message) => {
          const alertHelper = new AlertHelper({ i18n });
          alertHelper.showError(message);
          // If request for lift study fails, push the user back from edit form to lift studies grid
          send(WIZARD_EVENTS.CANCEL);
        };
        const onSuccess = async (liftExperiment) => {
          // Handle the case where user manually enters url for an uneditable lift study
          if (liftExperiment.Status !== STATUS_SCHEDULED) {
            onFailure(errorMessages.STATUS_INVALID_FOR_EDIT);
          }

          const uetTags = {
            both: [],
            conversion: [],
            visit: [],
          };
          const study = {
            ...liftExperiment,
            ...parseMeasurementTypeEnum(liftExperiment.MeasurementType),
          };

          if (study.useVisitLift && study.useConversionLift) {
            uetTags.both = [...study.UETTags];
          } else if (study.useVisitLift) {
            uetTags.visit = [...study.UETTags];
          } else if (study.useConversionLift) {
            uetTags.conversion = [...study.UETTags];
          }

          const response = await fetchCampaignsByIdEDM(scope.values, study.Campaigns);
          const selectedCampaigns = response.value;
          const selectedCampaignsById = {};
          selectedCampaigns.forEach((campaign) => {
            selectedCampaignsById[campaign.Id] = campaign;
          });

          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
            isInitialized: true,
            experimentName: study.Name,
            startDate: study.StartDate,
            country: study.Country,
            useSearchLift: study.useSearchLift,
            selectedKeywords: [],
            keywordsMethod: ADD_KEYWORDS_BY_ENTRY,
            typedKeywordString: study.Keywords.join(i18n.getString(_TL_(', '))),
            searchCid: study.SearchCustomerId || '',
            useVisitConversionLift: study.useVisitLift || study.useConversionLift,
            useVisitLift: study.useVisitLift,
            useConversionLift: study.useConversionLift,
            uetTags,
            selectedCampaignsById,
          });

          resolve({}); // still unsure how this resolution is being used
        };
        fetchExperiment(scope.values, { onSuccess, onFailure });
      }
    } else if (!wizardData.isInitialized) {
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
        isInitialized: true,
        isValid: false,
        experimentName: '',
        startDate: null,
        country: null,
        useSearchLift: true,
        selectedKeywords: [],
        keywordsMethod: ADD_KEYWORDS_BY_SELECTION,
        typedKeywordString: '',
        searchCid: '',
        useVisitConversionLift: false,
        useVisitLift: undefined,
        useConversionLift: undefined,
        uetTags: {
          both: [],
          conversion: [],
          visit: [],
        },
      });
    }

    resolve({});
  }), [i18n, isEdit, scope, send, wizardData.isInitialized]);

  const save = useCallback(() => {
    const { selectedCampaignsById } = wizardData;
    const count = Object.keys(selectedCampaignsById).length;
    const result = validateCampaignCount({ count, i18n });

    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, {
      isValid: result.isValid,
      selectCampaignsError: result.error,
      showError: !result.isValid,
    });

    if (!result.isValid) {
      return Promise.reject(new Error(result.error));
    }

    return new Promise((resolve) => {
      setTimeout(() => resolve({}), 800);
    });
  }, [i18n, send, wizardData]);

  return {
    load,
    save,
  };
};
